import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { checkAdminSession, loginAdmin } from './adminAuth';
import { API_URL } from './newsConfig';

const inputClassName =
  'w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cpre-new-yew/30 focus:border-cpre-new-yew';
const labelClassName = 'block text-sm font-medium text-cpre-new-yew mb-1';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/admin/news';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let cancelled = false;
    checkAdminSession(API_URL)
      .then((ok) => {
        if (!cancelled && ok) {
          setAuthenticated(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCheckingSession(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await loginAdmin(API_URL, username.trim(), password);
      setAuthenticated(true);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-cpre-pebble/20 flex items-center justify-center">
        <p className="text-cpre-new-yew font-medium">Loading…</p>
      </div>
    );
  }

  if (authenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="min-h-screen bg-cpre-pebble/20 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white shadow-sm border border-stone-200 rounded-2xl p-8">
        <h1 className="text-2xl font-heading font-bold text-cpre-new-yew mb-2">News Admin Login</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Sign in to manage news articles. This area is restricted to authorized administrators.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="admin-username" className={labelClassName}>
              Username
            </label>
            <input
              id="admin-username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputClassName}
            />
          </div>

          <div>
            <label htmlFor="admin-password" className={labelClassName}>
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClassName}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-2 bg-cpre-new-yew text-white rounded-full hover:bg-cpre-hill disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
